import { useRouteParams } from '@/context/routeParamsContext';
import * as dateTimeHelper from '@/helpers/dateTimeHelper';
import { prioritiseLounges } from '@/helpers/loungeHelper';
import * as loungeHelper from '@/helpers/loungeHelper';
import { normalizeProviderKey } from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useSelector } from 'react-redux';
import {
  WEEK_NAME_SUNDAY,
  LoungeTypes,
  CenturionProviders,
} from '@/constants/index';
import { WEB } from '@/constants/waitlistLounges';
import { useCdaasCenturionLoungeContent } from '@/hooks/cdaasHooks/cdaasFetchyeHooks';
import {
  useIsDefaultLoungebuddy,
  useOneCmsLocalePartners,
  useOneCmsLocaleResultsOverrideWithPlaceholder,
  useOneCmsLocaleDetailsAmenities,
  useOneCmsLocaleDateFormattingObj,
  useCommonContent,
} from '../oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useBdaasAirportLounges,
  useOneCmsLocaleAirportLounges,
  useBdaasAirportLoungesByExperienceType,
} from './loungesFetchyeHooks';

export const useOneCmsLoungeContent = () => {
  const { data } = useOneCmsLocaleAirportLounges();
  return data?.body ?? {};
};

export const useTranslatedAirportLounges = () => {
  const partners = useOneCmsLocalePartners();
  const { locale } = useSelector(getLocaleData);
  const {
    data: bdaasLoungesResponse,
    error: bdaasLoungesError,
    isLoading: bdaasLoungesIsLoading,
  } = useBdaasAirportLounges();
  const {
    data: oneCmsLocaleLoungesResponse,
    error: oneCmsLocaleLoungesError,
    isLoading: oneCmsLocaleLoungesIsLoading,
  } = useOneCmsLocaleAirportLounges();

  const isLoading =
    (!bdaasLoungesResponse && bdaasLoungesIsLoading) || // check response because isLoading could be true (maybe due to caching)
    oneCmsLocaleLoungesIsLoading;

  if (isLoading) {
    return {
      data: null,
      error: bdaasLoungesError || oneCmsLocaleLoungesError,
      isLoading,
    };
  }

  let prioritisedLounges = [];
  if (Array.isArray(bdaasLoungesResponse?.body)) {
    prioritisedLounges = prioritiseLounges(
      locale,
      bdaasLoungesResponse?.body,
      partners
    );
  }

  // Map through the prioritised lounges and replace name, description, and location details with the translated values from oneCmsLocaleLoungesResponse
  // if available (most lounges will not be present in OneCMS, API values will be used)
  const translatedLounges = prioritisedLounges.map((lounge) => {
    const oneCmsLounge = oneCmsLocaleLoungesResponse?.body?.[lounge.id];
    const isOpen = loungeHelper.isLoungeOpen(locale, lounge);
    const sortedAmenities = lounge.amenities?.toSorted(
      (a, b) => a.rank - b.rank
    );

    if (oneCmsLounge) {
      return {
        ...lounge,
        name: oneCmsLounge.name || lounge.name,
        description: oneCmsLounge.description || lounge.description,
        location: {
          ...lounge.location,
          terminal: oneCmsLounge.terminal || lounge.location?.terminal,
          terminal_directions:
            oneCmsLounge.terminal_directions ||
            lounge.location?.terminal_directions,
        },
        isOpen,
        amenities: sortedAmenities,
      };
    }

    return {
      ...lounge,
      isOpen,
      amenities: sortedAmenities,
    };
  });

  return {
    data: translatedLounges,
    error: bdaasLoungesError || oneCmsLocaleLoungesError,
    isLoading,
  };
};

export const useLoungeById = (loungeId) => {
  const lounges = useTranslatedAirportLounges();
  return lounges.data?.find((lounge) => lounge?.id === loungeId);
};

export const useLoungeProviders = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { providers } = useCommonContent();
  const { data: centurionLoungeContent } = useCdaasCenturionLoungeContent();
  const airportCode = lounge?.airport?.iata;

  if (!airportCode) return [];

  const loungeContentSlug =
    lounge.experience_type === LoungeTypes.SIDECAR
      ? `${airportCode.toLowerCase()}-sidecar`
      : airportCode.toLowerCase();
  const cdaasLounge = centurionLoungeContent?.body?.find(
    ({ url }) => url === loungeContentSlug
  );
  const providerReferences = lounge.providers ?? cdaasLounge?.providers ?? [];
  const providerCatalog = providers?.items ?? [];

  return providerReferences.reduce((matchedProviders, providerReference) => {
    const referenceKey =
      typeof providerReference === 'object' && providerReference !== null
        ? normalizeProviderKey(providerReference.providerKey) ||
          normalizeProviderKey(providerReference.name)
        : normalizeProviderKey(providerReference);
    if (!referenceKey) return matchedProviders;

    const matchingProvider = providerCatalog.find(
      (provider) =>
        normalizeProviderKey(provider?.providerKey) === referenceKey ||
        normalizeProviderKey(provider?.name) === referenceKey
    );

    if (matchingProvider) matchedProviders.push(matchingProvider);
    return matchedProviders;
  }, []);
};

export const useIsLoungeOpen = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { locale } = useSelector(getLocaleData);
  return loungeHelper.isLoungeOpen(locale, lounge);
};

export const useLoungeAirportLinkUrl = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { product, airportCode } = useRouteParams();
  const airportCodeForLink = !lounge ? airportCode : lounge?.airport?.iata;

  return `${product}/${airportCodeForLink}/`;
};

export const useLoungeDetailsCarouselImages = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const overrideWithPlaceholder =
    useOneCmsLocaleResultsOverrideWithPlaceholder();
  const oneCmsBaseImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  let images = [
    {
      imgUrl: `${oneCmsBaseImgUrl}placeholder.jpg`,
      imgId: 'placeholder',
    },
  ];
  if (lounge?.images?.length > 0 && !overrideWithPlaceholder) {
    images = lounge?.images.map((img) => ({
      imgUrl: img?.images?.full,
      imgId: img?.object_id,
    }));
  }
  return images;
};

export const useLoungeName = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.name;
};

/**
 * Returns waitlist information for a lounge by its ID.
 *
 * @param {string} loungeId - The ID of the lounge.
 * @returns {null | {
 *   is_wait_list_on: boolean,
 *   reservation_duration_seconds: number,
 *   type: 'WEB' | 'NATIVE',
 *   url: string,
 *   hideEstimatedWaitTime: boolean,
 *   estimatedWaitBracket: {
 *     cardMemberMin: string,
 *     cardMemberMax: string
 *   },
 *   bookingLoungeId: string
 * }} The waitlist information object or null if not available.
 */
export const useLoungeWaitList = (loungeId) => {
  const lounge = useLoungeById(loungeId);

  if (!lounge?.checkin) return null;

  // Normalize checking data
  const {
    estimated_wait_bracket: estimatedWaitBracket,
    hide_estimated_wait_time: hideEstimatedWaitTime,
    ...restCheckinData
  } = lounge.checkin;

  let estimatedWaitBracketNormalized = null;

  if (estimatedWaitBracket) {
    const { card_member_min: cardMemberMin, card_member_max: cardMemberMax } =
      estimatedWaitBracket;
    estimatedWaitBracketNormalized = { cardMemberMin, cardMemberMax };
  }

  return {
    ...restCheckinData,
    bookingLoungeId: lounge.booking_lounge_id,
    estimatedWaitBracket: estimatedWaitBracketNormalized,
    hideEstimatedWaitTime,
  };
};

const useIsCurrentLoungeOpen = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { locale } = useSelector(getLocaleData);
  const format = useOneCmsLocaleDateFormattingObj();

  return loungeHelper.getLoungeCurrentStatus(locale, lounge, format.timeFormat)
    .isOpen;
};

export const useIsWaitListEnabledLounge = (loungeId) => {
  const waitListLoungeInfo = useLoungeWaitList(loungeId);
  const isLoungeOpen = useIsCurrentLoungeOpen(loungeId);

  // Type WEB is used to indicate that a lounge is hooked up to Centurion Lounge Queue aka Waitlist
  return waitListLoungeInfo?.type === WEB && isLoungeOpen;
};

export const useLoungeAirportIata = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.airport.iata;
};

export const useLoungeProviderId = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.access_rules[0].provider_id;
};

export const useLoungeDescription = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const firstNoteIdx = lounge?.description?.search(
    /\bnote\b|注意事項|ご注意|注|hinweis|remarque|attenzione|nota|附註|注意/i
  );

  return lounge?.description?.slice(0, firstNoteIdx);
};

export const useLoungePrioritisedAccessRules = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.access_rules || null; // use existing backend sorting since it is already prioritised, to remain consistent with mobile
};

export const usePoliciesByLoungeId = (loungeId) => {
  const contentByLoungeMap = useOneCmsLoungeContent();
  if (!contentByLoungeMap?.[loungeId]) {
    return {};
  }

  const {
    guest_policy = '',
    guestPolicyLabel = '',
    access_requirements = '',
    accessPolicyLabel = '',
  } = contentByLoungeMap[loungeId];

  return {
    accessPolicyLabel,
    guestPolicyLabel,
    access_requirements,
    guest_policy,
  };
};

export const useLoungeAccessListMatchedWithLocaleProviders = (loungeId) => {
  const accessRules = useLoungePrioritisedAccessRules(loungeId);
  const productSpecificPoliciesForAllLounges = accessRules[0] || {};
  const marketProviders = useOneCmsLocalePartners();
  const loungeSpecificPolicies = usePoliciesByLoungeId(loungeId);
  const defaultLoungeBuddy = useIsDefaultLoungebuddy();
  const { experience_type: experienceType } = useLoungeById(loungeId);

  if (accessRules?.length > 0) {
    if (defaultLoungeBuddy) {
      return {
        id: productSpecificPoliciesForAllLounges.id,
        accessLevel: productSpecificPoliciesForAllLounges.access_requirements,
        guestPolicy: productSpecificPoliciesForAllLounges.guest_policy,
      };
    }

    /*
      For Sidecar Lounges, the `provider_id` from the lounges api will come in as `PROVIDER_CENTURION_{COUNTRY}`. (US, IN, AU etc)
      Since INTL markets (defaultLoungeBuddy is false) display the access/guest policy based on provider_id from OneCMS, we need to add `_SIDECAR` to the provider_id
      so Sidecar Lounges can have their own access/guest policy (not the same one as the Centurion Lounge)
    */
    const loungePrimaryProviderId =
      experienceType === LoungeTypes.SIDECAR
        ? accessRules[0]?.provider_id?.replace(
            'CENTURION',
            'CENTURION_SIDECAR'
          ) || CenturionProviders.UnitedStatesSidecar
        : accessRules[0]?.provider_id;

    const loungePrimaryProviders = loungePrimaryProviderId?.split(',') ?? [];
    const matchingProvider = marketProviders.find(
      ({ provider_id: providerId }) => {
        let parsedProviderList = providerId?.split(',') ?? [];
        parsedProviderList = parsedProviderList.map((provider) =>
          provider.trim()
        );
        return parsedProviderList.some((provider) =>
          loungePrimaryProviders.includes(provider)
        );
      }
    );

    if (matchingProvider) {
      // if no loungeSpecificPolicies.access_requirements, use the matching provider's access_guests (from `the-platinum-card.json`)
      const matchedPolicies = {
        id: matchingProvider?.provider_id,
        accessLevel:
          loungeSpecificPolicies.access_requirements ||
          matchingProvider?.access_guests,
      };

      if (loungeSpecificPolicies.guest_policy) {
        matchedPolicies.guestPolicy = loungeSpecificPolicies.guest_policy;
      }
      return matchedPolicies;
    }
  }
  return {};
};

export const useLoungeAmenitiesWithLocaleTranslations = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const loungeAmenities = lounge?.amenities;
  const localeAmenities = useOneCmsLocaleDetailsAmenities(loungeId);
  return loungeAmenities.reduce((acc, loungeAmenity) => {
    const matchingLocaleAmenity = localeAmenities.find(
      (localeAmenity) => localeAmenity.name === loungeAmenity.id
    );
    if (matchingLocaleAmenity) {
      acc.push({
        // the ID must come from the lounges API so the market can't change it in their locale file
        id: loungeAmenity.id,
        label: matchingLocaleAmenity?.label,
      });
    }
    return acc;
  }, []);
};

export const useLoungeHoursInLocaleFormat = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const dateFormat = useOneCmsLocaleDateFormattingObj();
  const { locale } = useSelector(getLocaleData);
  const openingHours = lounge?.hours;
  if (loungeHelper.isNoteInsteadOfOpenCloseTimes(openingHours)) {
    return openingHours.monday[0].note;
  }

  const sundayIndex = dateFormat.weekOrder.indexOf(WEEK_NAME_SUNDAY);

  // Create a new array of translated days of the week mapping the weekOrder list
  const mappedTranslatedDaysOfTheWeek = dateFormat.weekOrder.map((_, i) => {
    const translatedIndex = (i + 7 - sundayIndex) % 7;
    return lounge.week_days[translatedIndex];
  });

  const formatLoungeTime = (timeString) =>
    timeString
      ? dateTimeHelper.formatLoungeTimeFromTimeString({
          timeString,
          loungeTimeZone: lounge.airport.time_zone,
          format: dateFormat.timeFormat,
          locale,
        })
      : null;

  return Object.keys(openingHours)
    .sort(
      (a, b) =>
        Number.parseInt(dateFormat.weekOrder?.indexOf(a), 10) -
        Number.parseInt(dateFormat.weekOrder?.indexOf(b), 10)
    )
    .map((day, index) => ({
      dayTranslated: mappedTranslatedDaysOfTheWeek[index],
      day,
      firstOpenTime: formatLoungeTime(openingHours[day][0].open),
      firstCloseTime: formatLoungeTime(openingHours[day][0].close),
      secondOpenTime: formatLoungeTime(openingHours[day][1]?.open),
      secondCloseTime: formatLoungeTime(openingHours[day][1]?.close),
    }));
};

export const useLoungeTerminal = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.terminal;
};

export const useLoungeDirections = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.terminal_directions;
};

export const useLoungeAirside = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.location?.airside;
};

export const useLoungeImportantInformation = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  return lounge?.important_information;
};

export const useLoungesByExperienceType = (experienceType) => {
  const { data, error, isLoading } =
    useBdaasAirportLoungesByExperienceType(experienceType);

  return {
    data: data?.body ?? null,
    error,
    isLoading,
  };
};

export const useLoungeMapImages = (loungeId) => {
  const lounge = useLoungeById(loungeId);
  const { data } = useCdaasCenturionLoungeContent();
  const iata = lounge?.airport?.iata?.toLowerCase();
  const centurionLounge = data?.body?.find((l) => l.url === iata);
  return {
    smallMapImage: centurionLounge?.info?.smallMapImage,
    largeMapImage: centurionLounge?.info?.largeMapImage,
  };
};
